import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, Search, Building2, User, Package, ReceiptText } from 'lucide-react';
import MultiSelectFilter from 'components/common/MultiSelectFilter';

const EntradasNFTable = ({ 
  data = [], 
  loading = false,
  maxHeight = 'max-h-96',
  showHeader = true,
  stickyHeader = true,
  className = '',
  onRowClick = null,
  emptyMessage = 'Nenhum dado encontrado',
  idField = 'ID ENTRADA',
  idLabel = 'ID ENTRADA',
  showProductFilter = false,
  showSummaryTotals = false,
}) => {
  const [sortConfig] = useState({ key: null, direction: 'asc' });
  const [busca, setBusca] = useState('');
  // Seleção múltipla: lista vazia significa "todos".
  const [filtroProduto, setFiltroProduto] = useState([]);
  const [filtroFornecedor, setFiltroFornecedor] = useState([]);
  const [filtroFuncionario, setFiltroFuncionario] = useState([]);

  // Extrair listas únicas para filtros
  const fornecedores = useMemo(() => {
    return [...new Set(data.map(item => item.FORNECEDOR).filter(Boolean))].sort();
  }, [data]);

  // Produto do cadastro: dezenas de opções no mês, contra centenas de grafias na descrição da nota.
  const produtos = useMemo(() => {
    return [...new Set(data.map(item => item.PRODUTO).filter(Boolean))].sort();
  }, [data]);

  const funcionarios = useMemo(() => {
    return [...new Set(data.map(item => item.FUNCIONARIO).filter(Boolean))].sort();
  }, [data]);

  // Filtrar dados
  const dadosFiltrados = useMemo(() => {
    return data.filter(item => {
      const matchBusca = 
        item[idField]?.toString().includes(busca.toLowerCase()) ||
        (item['DESCRIÇÃO NF'] && String(item['DESCRIÇÃO NF']).toLowerCase().includes(busca.toLowerCase())) ||
        (item.DOCUMENTO && String(item.DOCUMENTO).toLowerCase().includes(busca.toLowerCase())) ||
        (item.FORNECEDOR && String(item.FORNECEDOR).toLowerCase().includes(busca.toLowerCase())) ||
        (item.CIDADE && String(item.CIDADE).toLowerCase().includes(busca.toLowerCase())) ||
        (item.FUNCIONARIO && String(item.FUNCIONARIO).toLowerCase().includes(busca.toLowerCase()));
      
      const matchFornecedor = filtroFornecedor.length === 0 || filtroFornecedor.includes(item.FORNECEDOR);
      const matchFuncionario = filtroFuncionario.length === 0 || filtroFuncionario.includes(item.FUNCIONARIO);
      const matchProduto = filtroProduto.length === 0 || filtroProduto.includes(item.PRODUTO);
      
      return matchBusca && matchFornecedor && matchFuncionario && matchProduto;
    });
  }, [data, busca, filtroProduto, filtroFornecedor, filtroFuncionario, idField]);

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return dadosFiltrados;
    
    return [...dadosFiltrados].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [dadosFiltrados, sortConfig]);

  // Só Entradas de NF traz a descrição da nota; a Movimentação Interna usa a mesma tabela sem ela.
  const temDescricaoNf = useMemo(() => data.some(item => item['DESCRIÇÃO NF']), [data]);

  const totalValorItemFornecedor = useMemo(() => {
    if (filtroFornecedor.length === 0) return 0;
    return dadosFiltrados.reduce((total, item) => (
      total + parseFloat(item?.['VALOR ITEM'] || 0)
    ), 0);
  }, [dadosFiltrados, filtroFornecedor]);

  const totaisDoRecorte = useMemo(() => {
    const notasContadas = new Set();

    return dadosFiltrados.reduce((totais, item, index) => {
      const quantidade = Number(item?.QUANTIDADE || 0);
      const valorUnitario = Number(item?.['VALOR UNITÁRIO'] || 0);
      const valorItem = Number(item?.['VALOR ITEM'] || 0);
      const notaKey = String(item?.[idField] ?? item?.DOCUMENTO ?? index);

      totais.itens += Number.isFinite(quantidade) ? quantidade : 0;
      totais.valorUnitario += Number.isFinite(valorUnitario) ? valorUnitario : 0;
      totais.valorItem += Number.isFinite(valorItem) ? valorItem : 0;

      if (!notasContadas.has(notaKey)) {
        const valorNf = Number(item?.['VALOR NF'] || 0);
        totais.valorNf += Number.isFinite(valorNf) ? valorNf : 0;
        notasContadas.add(notaKey);
      }

      return totais;
    }, { itens: 0, valorUnitario: 0, valorItem: 0, valorNf: 0 });
  }, [dadosFiltrados, idField]);

  const formatarMoeda = (valor) => `R$ ${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  if (loading) {
    return (
      <div className={`bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] overflow-hidden ${className}`}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-[color:var(--surface-muted)] rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-[color:var(--surface-muted)] rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] overflow-hidden ${className}`}>
      {showHeader && (
        <div className="px-4 sm:px-6 py-4 border-b border-[color:var(--border)] bg-[color:var(--surface-muted)]/35">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h3 className="text-base font-semibold text-[color:var(--text-primary)] tracking-tight">
              <span className="text-sm text-[color:var(--text-muted)] font-medium">
                {dadosFiltrados.length} de {data.length} registros
              </span>
            </h3>
            
            {/* Componente de Pesquisa Compacto */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${showProductFilter ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-3 w-full lg:w-auto`}>
              <div className="w-full sm:min-w-[240px]">
                <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1">
                  <Search className="h-3 w-3" />
                  Buscar
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-[color:var(--text-muted)]" />
                  </div>
                  <input
                    type="text"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder={temDescricaoNf
                      ? `Buscar ${idLabel.toLowerCase()}, descrição da nota, fornecedor...`
                      : `Buscar ${idLabel.toLowerCase()}, documento, fornecedor...`}
                    title={temDescricaoNf
                      ? 'Busca por id, descrição da nota, documento, fornecedor, cidade ou funcionário'
                      : 'Busca por id, documento, fornecedor, cidade ou funcionário'}
                    className="block w-full pl-9 pr-3 py-2 text-sm border border-[color:var(--border)] rounded-md leading-5 bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  />
                </div>
              </div>

              {showProductFilter && (
                <div className="w-full sm:min-w-[220px]">
                  <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1" htmlFor="filtro-produto-nf">
                    <Package className="h-3 w-3" />
                    Produto
                  </label>
                  <MultiSelectFilter
                    buttonId="filtro-produto-nf"
                    options={produtos}
                    values={filtroProduto}
                    onChange={setFiltroProduto}
                    placeholder="Todos os produtos"
                    label="Produtos"
                    icon={Package}
                  />
                </div>
              )}

              <div className="w-full sm:min-w-[200px]">
                <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1" htmlFor="filtro-fornecedor-nf">
                  <Building2 className="h-3 w-3" />
                  Fornecedor
                </label>
                <MultiSelectFilter
                  buttonId="filtro-fornecedor-nf"
                  options={fornecedores}
                  values={filtroFornecedor}
                  onChange={setFiltroFornecedor}
                  placeholder="Todos os fornecedores"
                  label="Fornecedores"
                  icon={Building2}
                />
              </div>
              
              <div className="w-full sm:min-w-[200px]">
                <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1" htmlFor="filtro-funcionario-nf">
                  <User className="h-3 w-3" />
                  Funcionário
                </label>
                <MultiSelectFilter
                  buttonId="filtro-funcionario-nf"
                  options={funcionarios}
                  values={filtroFuncionario}
                  onChange={setFiltroFuncionario}
                  placeholder="Todos os funcionários"
                  label="Funcionários"
                  icon={User}
                />
              </div>
            </div>
          </div>
          {filtroFornecedor.length > 0 && (
            <div className="mt-3 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2">
              <p className="text-xs text-[color:var(--text-muted)]">
                {filtroFornecedor.length > 1 ? 'Total dos fornecedores selecionados:' : 'Total do fornecedor selecionado:'}
                <span className="ml-2 font-semibold text-[color:var(--status-good)]">
                  R$ {totalValorItemFornecedor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </p>
            </div>
          )}
          {showSummaryTotals && (
            <section className="mt-4 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-3" aria-label="Totais do detalhamento filtrado">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                <ReceiptText className="h-4 w-4 text-[color:var(--accent)]" />
                Totais do recorte
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-md bg-[color:var(--surface-muted)]/60 px-3 py-2">
                  <p className="text-xs text-[color:var(--text-muted)]">Qtd. de itens</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-[color:var(--text-primary)]">{Math.round(totaisDoRecorte.itens).toLocaleString('pt-BR')}</p>
                </div>
                <div className="rounded-md bg-[color:var(--surface-muted)]/60 px-3 py-2">
                  <p className="text-xs text-[color:var(--text-muted)]">Valor unitário</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-[color:var(--text-primary)]">{formatarMoeda(totaisDoRecorte.valorUnitario)}</p>
                </div>
                <div className="rounded-md bg-[color:var(--surface-muted)]/60 px-3 py-2">
                  <p className="text-xs text-[color:var(--text-muted)]">Valor dos itens</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-[color:var(--text-primary)]">{formatarMoeda(totaisDoRecorte.valorItem)}</p>
                </div>
                <div className="rounded-md border border-[color:var(--accent)]/30 bg-[color:var(--accent)]/10 px-3 py-2">
                  <p className="text-xs text-[color:var(--text-muted)]">Valor das NFs</p>
                  <p className="mt-0.5 font-semibold tabular-nums text-[color:var(--status-good)]">{formatarMoeda(totaisDoRecorte.valorNf)}</p>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
      
      <div className={`${maxHeight} overflow-y-auto overflow-x-hidden`}>
        <div className="w-full">
          <table className="w-full table-fixed divide-y divide-[color:var(--border)]" style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead className={`bg-[color:var(--surface-muted)]/95 backdrop-blur-sm ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
              <tr>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[5%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">{idLabel}</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === idField && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === idField && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[7%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Data</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'DATA' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'DATA' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[10%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Funcionário</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'FUNCIONARIO' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'FUNCIONARIO' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[12%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Produto</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'PRODUTO' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'PRODUTO' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[14%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Documento</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'DOCUMENTO' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'DOCUMENTO' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[12%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Fornecedor</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'FORNECEDOR' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'FORNECEDOR' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[8%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Cidade</span>
                    <div className="flex flex-col">
                      <ChevronUp
                        className={`w-3 h-3 ${
                          sortConfig.key === 'CIDADE' && sortConfig.direction === 'asc'
                            ? 'text-[color:var(--accent)]'
                            : 'text-[color:var(--text-muted)]'
                        }`}
                      />
                      <ChevronDown
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'CIDADE' && sortConfig.direction === 'desc'
                            ? 'text-[color:var(--accent)]'
                            : 'text-[color:var(--text-muted)]'
                        }`}
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[6%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Qtd</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'QUANTIDADE' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'QUANTIDADE' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[8%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Valor Unit.</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'VALOR UNITÁRIO' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'VALOR UNITÁRIO' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[8%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Valor Item</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'VALOR ITEM' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'VALOR ITEM' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
                <th className="px-2 py-1 text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider w-[10%]">
                  <div className="flex items-center space-x-1">
                    <span className="break-words">Valor NF</span>
                    <div className="flex flex-col">
                      <ChevronUp 
                        className={`w-3 h-3 ${
                          sortConfig.key === 'VALOR NF' && sortConfig.direction === 'asc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                      <ChevronDown 
                        className={`w-3 h-3 -mt-1 ${
                          sortConfig.key === 'VALOR NF' && sortConfig.direction === 'desc' 
                            ? 'text-[color:var(--accent)]' 
                            : 'text-[color:var(--text-muted)]'
                        }`} 
                      />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center text-[color:var(--text-muted)]">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                sortedData.map((row, index) => (
                  <tr 
                    key={index} 
                    className={`${index % 2 === 0 ? 'bg-[color:var(--surface)]' : 'bg-[color:var(--surface-muted)]/25'} hover:bg-[color:var(--surface-muted)]/80 transition-colors duration-150 ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick && onRowClick(row, index)}
                  >
                    <td className="px-2 py-1 text-xs">
                      <div className="font-medium text-[color:var(--accent)] break-words">
                        {row[idField]}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs">
                      <div className="break-words">
                        {row.DATA}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs">
                      <div className="break-words font-medium text-[color:var(--text-primary)]">
                        {row.FUNCIONARIO}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs" >
                      <div className="break-words font-medium text-[color:var(--text-primary)]">
                        {row.PRODUTO}
                      </div>
                      {row['DESCRIÇÃO NF'] && row['DESCRIÇÃO NF'] !== row.PRODUTO && (
                        <div className="mt-0.5 break-words text-[11px] text-[color:var(--text-muted)]" title="Descrição do item na nota">
                          {row['DESCRIÇÃO NF']}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1 text-xs" >
                      <div className="break-all">
                        {row.DOCUMENTO}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs" >
                      <div className="break-words">
                        {row.FORNECEDOR}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs" >
                      <div className="break-words">
                        {row.CIDADE}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs text-right" >
                      <div className="break-words font-medium text-[color:var(--text-primary)] tabular-nums">
                        {Math.round(Number(row.QUANTIDADE || 0)).toLocaleString('pt-BR', { 
                          minimumFractionDigits: 0, 
                          maximumFractionDigits: 0 
                        })}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs text-right" >
                      <div className="break-words font-bold text-[color:var(--accent)] tabular-nums">
                        R$ {parseFloat(row['VALOR UNITÁRIO'] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs text-right" >
                      <div className="break-words font-bold text-[color:var(--text-primary)] tabular-nums">
                        R$ {parseFloat(row['VALOR ITEM'] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-xs text-right" >
                      <div className="break-words font-bold text-[color:var(--status-good)] tabular-nums">
                        R$ {parseFloat(row['VALOR NF'] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {dadosFiltrados.length > 0 && (
        <div className="px-6 py-2 bg-[color:var(--surface-muted)] border-t border-[color:var(--border)]">
          <div className="flex items-center justify-between text-xs text-[color:var(--text-muted)]">
            <span>{dadosFiltrados.length} registros</span>
            <span>Use a rolagem vertical para navegar pelos dados ↑ ↓</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EntradasNFTable;
